/**
 * Starting text for the universal Platform Services Agreement.
 *
 * Adapted from the onboarding agreement template retired in #2912
 * (frontend/packages/shared/src/utils/agreementTemplate.ts). That template was
 * rendered per business; default terms publish one exact document that every
 * following business accepts, so the business is identified by its acceptance
 * record rather than named in the text. The wording is still an UNREVIEWED
 * DRAFT: it starts counsel review, and publishing still needs the editor's
 * legal approval, legal-party and jurisdiction references.
 */

import { PLATFORM_LEGAL_DISPLAY_NAME } from "@bw/shared";
import { fractionToPercent, percentToFraction, type ContractTermsDraft } from "./contractTermsDraft";

export const PLATFORM_SERVICES_AGREEMENT_TEMPLATE_KEY = "platform-services-agreement";
export const PLATFORM_SERVICES_AGREEMENT_DRAFT_VERSION = "draft-2026-11";

const PLATFORM_NAME = PLATFORM_LEGAL_DISPLAY_NAME;
/** Matches the grandfather window applied when new default terms are issued. */
const NOTICE_DAYS = "sixty (60)";
const RULE = "-".repeat(72);
const DRAFT_BANNER = "DRAFT - PENDING AUTHORIZED LEGAL REVIEW. NOT APPROVED FOR PRODUCTION USE.";

/** "05", "4.50" and ".5" all read as the plain percentage the server stores. */
function feeLabel(commissionPercent: string): string {
  const fraction = percentToFraction(commissionPercent);
  return `${fraction === null ? commissionPercent.trim() : fractionToPercent(fraction)}%`;
}

function longDate(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? isoDate
    : parsed.toLocaleDateString("en-US", { timeZone: "UTC", year: "numeric", month: "long", day: "numeric" });
}

function sections(fee: string, effective: string, version: string): [string, string[]][] {
  return [
    ["Parties", [
      `This Platform Services Agreement ("Agreement") is entered into as of the later of ${effective} and the date the Business accepts it, by and between:`,
      `(a) ${PLATFORM_NAME} ("Platform"), a marketplace platform for badminton facility bookings; and`,
      "(b) the business organization that accepts this Agreement through the Platform, acting through its authorized representative (\"Business\"). The Business's legal name, trade name, representative, and the date and time of acceptance are recorded with its acceptance.",
    ]],
    ["Platform Services", [
      "The Platform provides the following services to the Business:",
      "(a) Marketplace listing - the Business's facility, courts, and available time slots are listed on the Platform for discovery by players.",
      "(b) Booking management - players can search, book, modify, and cancel court reservations through the Platform.",
      "(c) Payment processing - player payments are processed through Stripe Connect. The Business's connected Stripe account receives payments, and the Platform collects the applicable platform service fee as an application fee where supported.",
      "(d) Customer support - the Platform provides first-line support for booking-related inquiries from players.",
    ]],
    ["Platform Fee and Payment Processing Fees", [
      `3.1 The Platform charges a platform service fee of ${fee} on the gross booking amount (the total amount paid by the player, excluding taxes collected on behalf of the Business) for each completed booking.`,
      "3.2 Stripe payment processing fees, disputes, chargebacks, bank fees, and taxes are separate from the platform service fee and remain the responsibility of the Business unless a separate written agreement states otherwise.",
      "3.3 The platform service fee is collected automatically as an application fee where supported. No separate invoice is issued for the platform service fee; Stripe and platform fee records serve as the record of fees charged.",
      "3.4 Refunds issued to players for cancellations reduce the gross booking amount accordingly. If a full refund is issued, no platform service fee is charged for that booking. If a partial refund is issued, the platform service fee is recalculated on the adjusted amount.",
      "3.5 Changes to platform default rates do not modify this Agreement automatically. A new fee rate applies only if a new agreement version is issued and accepted or otherwise becomes effective under the notice terms in Section 5.",
    ]],
    ["Stripe Account and Payout Responsibility", [
      "4.1 Player payments are processed on the Business's connected Stripe account. The Platform does not hold player payment funds for later payout to the Business unless a separate written agreement states otherwise.",
      "4.2 Stripe payouts from the Business's connected Stripe account to the Business's bank account are governed by the Business's Stripe account settings and Stripe's applicable terms.",
      "4.3 The Business is responsible for maintaining valid Stripe onboarding information, bank account information, tax information, and required Stripe capabilities.",
      "4.4 The Business remains responsible for Stripe fees, disputes, chargebacks, refunds, negative balances, and taxes unless a separate written agreement states otherwise.",
    ]],
    ["Term and Changes", [
      `5.1 This Agreement is effective as of ${effective}, or the date the Business accepts it if later, and remains valid for one (1) year from that date, unless terminated earlier by either party in accordance with Section 8.`,
      "5.2 The Business must review and accept the then-current Agreement once each year for continued use of the Platform.",
      `5.3 The Platform may issue a new agreement version with at least ${NOTICE_DAYS} days' prior written notice to the Business. The Business's previously accepted terms remain in force during the notice period, and the new version applies once it is accepted or the notice period ends, as stated with that version.`,
      `5.4 This is version ${version} of the Agreement. The version identifier is included for record-keeping and audit purposes.`,
    ]],
    ["Business Responsibilities", [
      "The Business agrees to:",
      "(a) Maintain its facility in a safe, clean, and playable condition at all times during listed availability.",
      "(b) Honor all confirmed bookings made through the Platform, except in cases of force majeure or facility emergency.",
      "(c) Provide accurate and up-to-date information about its facility, including court availability, pricing, amenities, and operating hours.",
      "(d) Comply with all applicable local, state, and federal laws and regulations related to the operation of its facility.",
      "(e) Respond to player inquiries and complaints forwarded by the Platform within a reasonable timeframe.",
      "(f) Not circumvent the Platform by soliciting direct bookings from players who were introduced through the Platform.",
    ]],
    ["Limitation of Liability", [
      '7.1 THE PLATFORM IS PROVIDED "AS IS." TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE PLATFORM DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.',
      "7.2 The Platform shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities, arising out of or related to this Agreement.",
      "7.3 The Platform's total aggregate liability under this Agreement shall not exceed the total platform service fees paid by the Business to the Platform in the twelve (12) months preceding the event giving rise to the claim.",
      "7.4 The Business is solely responsible for the safety and condition of its facility and for any injuries, damages, or losses that occur on its premises.",
    ]],
    ["Termination", [
      "8.1 Either party may terminate this Agreement for any reason by providing thirty (30) days' written notice to the other party.",
      "8.2 The Platform may terminate this Agreement immediately and without notice if the Business: (a) breaches any material term of this Agreement; (b) engages in fraudulent or illegal activity; (c) receives repeated verified complaints from players regarding safety or misrepresentation; or (d) fails to honor confirmed bookings without reasonable justification.",
      "8.3 Completed transactions, refunds, disputes, chargebacks, and application fees remain subject to Stripe processing and the terms accepted before termination.",
      "8.4 Sections 7 (Limitation of Liability) and 9 (Governing Law) shall survive termination of this Agreement.",
    ]],
    ["Governing Law", [
      "This Agreement shall be governed by and construed in accordance with the laws of the State of California, United States of America, without regard to its conflict of law provisions. Any disputes arising under this Agreement shall be resolved in the state or federal courts located in San Francisco County, California.",
    ]],
    ["Entire Agreement", [
      "This Agreement constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior or contemporaneous communications, representations, or agreements, whether oral or written. No amendment or modification of this Agreement shall be effective unless made in writing and accepted by both parties.",
    ]],
  ];
}

/** Plain-text agreement for the given fee, effective date and version label. */
export function renderPlatformServicesAgreementDraft({
  commissionPercent,
  effectiveDate,
  versionLabel,
}: {
  commissionPercent: string;
  effectiveDate: string;
  versionLabel: string;
}): string {
  const effective = longDate(effectiveDate);
  const lines = [
    DRAFT_BANNER,
    "",
    "PLATFORM SERVICES AGREEMENT",
    `Version: ${versionLabel}`,
    `Effective Date: ${effective}`,
    "",
    RULE,
  ];
  sections(feeLabel(commissionPercent), effective, versionLabel).forEach(([title, paragraphs], index) => {
    lines.push("", `${index + 1}. ${title.toUpperCase()}`, "");
    for (const paragraph of paragraphs) lines.push(paragraph, "");
    lines.push(RULE);
  });
  lines.push(
    "",
    "ACKNOWLEDGED AND AGREED:",
    "",
    `Platform: ${PLATFORM_NAME}`,
    "Business: the organization that accepts this Agreement through the Platform",
    `Agreement Version: ${versionLabel}`,
    "",
    "Electronic acceptance records the Business, its representative, the date and time, this exact agreement version and its hash, and the platform service fee shown with it.",
  );
  return lines.join("\n");
}

/**
 * Why the draft text no longer matches the form's economics, or null. Only
 * applies to text that still carries the draft's fee and date statements.
 */
export function platformServicesAgreementDraftMismatch(
  values: Pick<ContractTermsDraft, "commissionPercent" | "effectiveDate" | "documentContent">,
): string | null {
  const fee = /platform service fee of ([^ ]+%) on the gross booking amount/.exec(values.documentContent)?.[1];
  const date = /^Effective Date: (.+)$/m.exec(values.documentContent)?.[1];
  if (fee !== undefined && fee !== feeLabel(values.commissionPercent)) {
    return `The agreement states a ${fee} platform service fee, but the commission rate is ${feeLabel(values.commissionPercent)}.`;
  }
  if (date !== undefined && date !== longDate(values.effectiveDate)) {
    return `The agreement's effective date is ${date}, but these terms take effect ${longDate(values.effectiveDate)}.`;
  }
  return null;
}

/**
 * Document fields the draft decides. Legal-party, jurisdiction and approval
 * references stay as entered: they record decisions made outside the app.
 */
export function platformServicesAgreementDraftFields(
  values: Pick<ContractTermsDraft, "commissionPercent" | "effectiveDate">,
  versionLabel: string,
): Pick<ContractTermsDraft, "documentContent" | "documentTemplateKey" | "documentTemplateVersion" | "documentRenderingVersion" | "language"> {
  return {
    documentContent: renderPlatformServicesAgreementDraft({ ...values, versionLabel }),
    documentTemplateKey: PLATFORM_SERVICES_AGREEMENT_TEMPLATE_KEY,
    documentTemplateVersion: PLATFORM_SERVICES_AGREEMENT_DRAFT_VERSION,
    documentRenderingVersion: "plain-text-v1",
    language: "en-US",
  };
}
